/**
 * Home (morning brief) route per spec section 15.10. The home view
 * lays out, top to bottom:
 *
 *   1. Greeting strip (date eyebrow, H1, commit meta)
 *   2. Optional banners (zero-day flag, 3pm uncleared queue)
 *   3. Sales queue (dominant section)
 *   4. Two-column grid: pipeline glance (left), recent wins (right)
 *   5. Today's frogs (compact list)
 *   6. Quick capture (single input at the bottom)
 *
 * All sections consume `/api/v1/morning-brief` in a single round trip
 * per the M10 acceptance criteria.
 */

import { createFileRoute } from '@tanstack/react-router';

import { useMorningBrief } from '@/lib/api/sales';
import { Greeting } from '@/components/home/Greeting';
import { PipelineGlance } from '@/components/home/PipelineGlance';
import { RecentWins } from '@/components/home/RecentWins';
import { Frogs } from '@/components/home/Frogs';
import { SalesQueue } from '@/components/sales/SalesQueue';
import { QuickCapture } from '@/components/tasks/QuickCapture';

export const Route = createFileRoute('/')({
  component: HomeView,
});

function HomeView() {
  const briefQuery = useMorningBrief();

  if (briefQuery.isLoading) {
    return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  }
  if (briefQuery.isError || !briefQuery.data) {
    return <p className="text-mx-body-sm text-mx-neg">Could not load the morning brief.</p>;
  }
  const brief = briefQuery.data;

  return (
    <div className="flex flex-col gap-mx-5">
      <Greeting
        greeting={brief.greeting}
        todayIso={brief.todayIso}
        userName="Peter"
        commitTarget={brief.dailyCommitTarget}
        committed={brief.committed.length}
        done={0}
      />

      {brief.zeroDayFlag && (
        <div
          role="status"
          data-testid="zero-day-banner"
          className="flex items-center justify-between border border-mx-grey-800 bg-mx-card px-mx-4 py-mx-3"
        >
          <span className="text-mx-body-sm text-mx-grey-300">
            <span className="mono-label mr-mx-2 text-mx-grey-mid">YESTERDAY</span>
            Zero sales actions completed. Worth checking if anything important got dropped.
          </span>
        </div>
      )}

      <section>
        <p className="mono-label text-mx-grey-mid">
          <span className="num">01</span>
          Today&rsquo;s sales queue
        </p>
        <div className="mt-mx-3">
          <SalesQueue />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-mx-4 lg:grid-cols-2">
        <PipelineGlance glance={brief.pipelineGlance} />
        <RecentWins wins={brief.recentWins} />
      </div>

      <Frogs tasks={brief.froggedTasks} />

      <section className="border border-mx-grey-800 bg-mx-card p-mx-4">
        <p className="mono-label text-mx-grey-mid">
          <span className="num">07</span>
          Quick capture
        </p>
        <div className="mt-mx-3">
          <QuickCapture />
        </div>
      </section>
    </div>
  );
}
